/**
 * Painting: where the pointer is on the Grid, which Cells a stroke covers, and
 * what a tap actually changes. Pure functions only; the pointer events, the
 * overlay and the socket are in `main.ts`.
 *
 * A spot off the Grid keeps its sign rather than wrapping or clamping. The Grid
 * wraps, but it is drawn as one still rectangle, so a stroke is a straight line
 * in what the eye sees: what falls outside is skipped, which is also what keeps
 * a stroke alive when it leaves the Grid and comes back.
 */

import type { PaintCells } from "./world.ts"
import { GRID_SIDE, isCellIndex } from "./world.ts"

/** A pointer position in Cell coordinates, in range or out of it. */
export interface Spot {
  readonly col: number
  readonly row: number
}

/** Hit-testing: one division per pointer event, from the canvas's own pixels. */
export const spotAt = (x: number, y: number, cellPx: number): Spot => ({
  col: Math.floor(x / cellPx),
  row: Math.floor(y / cellPx)
})

export const cellOf = (spot: Spot): number | undefined =>
  spot.col >= 0 && spot.col < GRID_SIDE && spot.row >= 0 && spot.row < GRID_SIDE
    ? spot.row * GRID_SIDE + spot.col
    : undefined

/**
 * The Cells a stroke covers on its way from one pointer spot to the next, the
 * spot it came from excluded: that Cell was painted while the pointer was in
 * it. Bresenham, so a fast stroke leaves no gaps.
 */
export const strokeCells = (from: Spot, to: Spot): ReadonlyArray<number> => {
  const cells: Array<number> = []
  const dx = Math.abs(to.col - from.col)
  const dy = Math.abs(to.row - from.row)
  const stepCol = from.col < to.col ? 1 : -1
  const stepRow = from.row < to.row ? 1 : -1
  let col = from.col
  let row = from.row
  let error = dx - dy
  while (col !== to.col || row !== to.row) {
    const doubled = 2 * error
    if (doubled > -dy) {
      error -= dy
      col += stepCol
    }
    if (doubled < dx) {
      error += dx
      row += stepRow
    }
    const cell = cellOf({ col, row })
    if (cell !== undefined) cells.push(cell)
  }
  return cells
}

/**
 * What a Brush actually writes: a tap sets a Cell to the Brush's value and does
 * nothing if it already holds it. No cycling, and no Brush but the eraser ever
 * writes 0.
 */
export const changesFor = (drawn: string, cells: ReadonlyArray<number>, value: number): PaintCells => {
  const wanted = String(value)
  return cells
    .filter((cell) => isCellIndex(cell) && drawn[cell] !== wanted)
    .map((cell) => [cell, value] as const)
}
